import React from "react";

const vehicleType = {
    CAR: "Carro",
    MOTORCYCLE: "Moto",
    TRUCK: "Caminhão",
    BUS: "Ônibus",
    VAN: "Van/Furgão",
    PICKUP: "Caminhonete",
    ARMORED : "Carro forte",
    TOWTRUCK: "Guincho",
    TRACTOR: "Máquina agrícola",
    FLATBED_TRUCK: "Caminhão prancha",
    TRUCK_TANK: "Caminhão tanque",
    UNDEFINED: 'Indefinido',
};

const vehicleSubtype = {
    FRONT_CAR: 'Dianteira',
    REAR_CAR: 'Traseira',

    FRONT_MOTORCYCLE: 'Dianteira',
    REAR_MOTORCYCLE: 'Traseira',
    NO_BAG: 'Sem baú ou mochila de motoboy',
    BAG: 'Com baú ou mochila de motoboy',
    'LOCK_TAMPERING|CORD_TAMPERING': 'Com placa obstruída',
    HAND_TAMPERING: 'Com placa obstruída pela mão',

    FRONT_TRUCK: 'Dianteira',
    REAR_TRUCK: 'Traseira'
};

const platePattern = {
    UNKNOWN: "Fora do padrão",
    DECORATIVE_MOPED: "Ciclomotor",
    INTERNATIONAL: "Outros países",
    LEGACY: "Cinza",
    MERCOSUL: "Mercosul"
};

const standardFlow = {
    ALL: "Todos",
    CLASSIFIED: "Somente classificados",
    NOT_CLASSIFIED: "Somente não classificados"
};

const occStatus = {
    ACTIVE: "Ativa",
    CONCLUDED: "Concluída"
};

const occClassif = {
    AUTOR: 'Autor',
    AVERIGUACAO_CLONE: 'Averiguação de clone',
    CITADO: 'Citado',
    ENVOLVIDO: 'Envolvido',
    FURTADO: 'Furtado',
    NAO_RELACIONADO: 'Não relacionado',
    OUTRO: 'Outro',
    PRODUTO: 'Produto',
    RECUPERADO: 'Recuperado',
    ROUBADO: 'Roubado',
    SUSPEICAO_FRACA: 'Suspeição fraca',
    SUSPEITO: 'Suspeito',
    VITIMA: 'Vítima'
};

export const getDateRangeSortValue = (element) => {
    if (!element?.date_range) return 0;

    const firstPart = element.date_range.split(" - ")[0];
    const match = firstPart.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/);

    if (!match) return 0;
    const [, d, m, y, h = "00", min = "00", s = "00"] = match;

    const date = new Date(Number(y), Number(m) - 1, Number(d), Number(h), Number(min), Number(s));
    return date.getTime();
};

export const booleanLabel = (value) => {
    if (value === null || value === undefined) return null;
    return value === "true" ? "Sim" : "Não";
};

export const isEmptyValue = (value) => {
    if (value === null || value === undefined || value === "" || value === '[]' || value === '{}' || value === "null") return true;
    if (Array.isArray(value) && value.length === 0) return true;

    return typeof value === "object" &&
        !Array.isArray(value) &&
        Object.keys(value).length === 0;
};

export const parseJsonValue = (value) => {
    if (typeof value !== "string") return value;

    try {
        return JSON.parse(value);
    } catch {
        return value;
    }
};

const parseVehicleType = (item) => {
    const match = item.match(/^(.+?)(?:\s*\((.*)\))?$/);

    if (!match) {
        return {
            type: item,
            subTypes: []
        };
    }

    const [, type, subTypes] = match;

    return {
        type: type.trim(),
        subTypes: subTypes ? subTypes.split(",").map((s) => s.trim().toUpperCase()) : []
    };
};

export const buildBrandModelsMap = (brandModels = []) => {
    if (!Array.isArray(brandModels)) return {};

    return brandModels.reduce((acc, item) => {
        if (!item?.labelName) return acc;

        const brand = item.brand && item.brand ? item.brand : "";
        const model = item.model && item.model ? item.model : "";
        const translatedLabel = [brand, model].filter(Boolean).join("/");

        acc[item.labelName] = translatedLabel || item.labelName;
        return acc;
    }, {});
};

export const translateValue = (field, value, brandModelsMap = {}) => {
    if (isEmptyValue(value)) return null;

    const translators = {
        vehicle_type: vehicleType,
        vehicle_subtype: vehicleSubtype,
        plate_pattern: platePattern,
        standard_flow: standardFlow,
        occ_status: occStatus,
        occ_classif: occClassif,
    };

    if (field === "vehicle_type") {
        return value.split(" + ").map((item) => {
            const { type, subTypes } = parseVehicleType(item);

            const translatedType = vehicleType[type] || type;
            if (subTypes.length === 0) return translatedType;

            const translatedSubTypes = subTypes.map((subType) => vehicleSubtype[subType] || subType);
            return `${translatedType} (${translatedSubTypes.join(", ")})`;
        }).join(" + ");
    }

    if (field === "occ_status") {
        if (typeof value === "string") {
            return value.split(",").map((item) => {
                const normalized = item.trim().toUpperCase();
                return occStatus[normalized] || item.trim();
            }).join(", ");
        }

        return value;
    }

    if (field === "occ_classif") {
        if (typeof value === "string") {
            return value.split(",").map((item) => {
                const normalized = item.trim().toUpperCase();
                return occClassif[normalized] || item.trim();
            }).join(", ");
        }

        return value;
    }

    if (translators[field]) {
        const commonValue = typeof value === "string" ? value.trim().toUpperCase() : value;
        return translators[field][commonValue] || value;
    }

    if (field === "city") {
        const parsed = parseJsonValue(value);

        if (Array.isArray(parsed)) {
            return parsed.map((item) => {
                const operator = item.operator === "NOT_CONTAINS" ? "Não contém" : "Contém";
                if (item.value === "[]") return operator;

                return `${operator} - ${item.value}`;
            }).join(", ");
        }

        if (typeof value === "string") return value.replace(/^CONTAINS\s*-\s*/i, "Contém - ").replace(/^NOT_CONTAINS\s*-\s*/i, "Não contém - ");
        return value;
    }

    if (field === "brand_model") {
        const parsed = parseJsonValue(value);

        if (Array.isArray(parsed)) {
            return parsed.map((item) => {
                const operator = item.operator === "NOT_CONTAINS" ? "Não contém" : "Contém";
                if (item.value === "[]") return operator;

                let translatedLabel = brandModelsMap[item.value] || item.value;
                if (item.value !== "undefined") {
                    translatedLabel = "Indefinido";
                    return `${operator} - ${translatedLabel}`;
                }

                return `${operator} - ${translatedLabel}`;
            }).join(", ");
        }

        if (typeof value === "string") {
            const contains = value.match(/^CONTAINS\s*-\s*(.*)$/i);
            const notContains = value.match(/^NOT_CONTAINS\s*-\s*(.*)$/i);

            if (contains) {
                const translated = brandModelsMap[contains[1]] || contains[1];
                return `Contém - ${translated}`;
            }

            if (notContains) {
                const translated = brandModelsMap[notContains[1]] || notContains[1];
                return `Não contém - ${translated}`;
            }
        }

        return brandModelsMap[value] || value;
    }

    return value;
};

export const buildRows = (label, value, className = "", colSpan = 2, hideEmpty = false) => {
    if (hideEmpty && isEmptyValue(value)) return null;

    return (
        <td colSpan={colSpan} className={`!border-0 !px-[20px] align-top ${className}`}>
            <div className="flex flex-row items-start gap-2 leading-[16px]">
                <span className="text-[#6c6969] whitespace-nowrap">
                    {label}:
                </span>

                <span className="break-words">
                    {value}
                </span>
            </div>
        </td>
    );
};

export const hasAnyValue = (...values) => {
    return values.some((value) => !isEmptyValue(value));
};

export const getValidations = (element) => ({
    generalValidate: hasAnyValue(
        element.plate_pattern,
        element.recent_passage,
        element.main_plate,
        element.wrong_way,
        element.clausura,
        element.expurgadas,
        element.with_image
    ),

    collectValidate: hasAnyValue(
        element.route
    ),

    classificationValidate: hasAnyValue(
        element.vehicle_type,
        element.vehicle_classif
    ),

    renavamValidate: hasAnyValue(
        element.registered_vehicles,
        element.brand_model,
        element.color,
        element.renavam_type,
        element.fabrication_year,
        element.model_year,
        element.renavam,
        element.chassi,
        element.city,
        element.uf,
        element.owner_cnpj,
        element.owner_name,
        element.possessor_cnpj,
        element.possessor_name
    ),

    profileValidate: hasAnyValue(
        element.total_passages,
        element.daily_passages,
        element.internal_stay,
        element.without_passages,
        element.external_stay,
        element.standard_flow,
        element.period_values
    ),

    analyticValidate: hasAnyValue(
        element.capture_direction,
        element.lane_number,
        element.camera_vehicle_brand,
        element.camera_vehicle_model,
        element.camera_vehicle_colors,
        element.no_seat_belt,
        element.driver_on_phone,
        element.vehicle_speed,
        element.motorcyclist_without_helmet
    ),

    othersValidate: hasAnyValue(
        element.occ_marker,
        element.encounter_related,
        element.read_not_match,
        element.occ_status,
        element.occ_classif,
        element.occ_nature,
        element.dispatch_reasons,
        element.labels,
        element.only_with_adr,
        element.adr_codes
    )
});